import { Button, Group } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { t } from "i18next"
import uniqBy from "lodash/uniqBy"
import { useAPIResource, useEnsureUsers, useUsers } from "@/hooks"
import type { TSession, TUserSession } from "@/types"
import { usersessionAPI } from "@/utils"
import { isEditable } from "@/utils/session"
import { Filters, TeamFilter, UserSearch } from "../controls"
import { StateFilter } from "../controls/StateFilter"
import { AdminOnly, icons } from "../utils"

interface IProps {
  session: TSession
  data: TUserSession[]
}

export function UserSessionsFilters({ session, data }: IProps) {
  const choosen = UserSearch.useValue()
  const volunteer = !data.find((us) => us.user_id === choosen)

  // Search among actual participants of the session (including deactivated users)
  const participantIds = data.map((us) => us.user_id).filter((id): id is string => !!id)
  useEnsureUsers(participantIds)
  const { users: activeUsers, findUsers } = useUsers()
  const participants = findUsers(participantIds)
  const searchUsers = uniqBy([...participants, ...activeUsers], "id").toSorted((a, b) =>
    (a.full_name || a.name).localeCompare(b.full_name || b.name),
  )

  const { create } = useAPIResource(usersessionAPI, {
    throwOnError: false,
    onError: () => {
      notifications.show({
        title: t("Error"),
        message: t("Could not add participant"),
        color: "red",
      })
      return false
    },
  })

  return (
    <Filters h={40}>
      <TeamFilter />
      <StateFilter />
      <Group ml="auto" gap="xs">
        <UserSearch users={searchUsers} />
        {isEditable(session) && (
          <AdminOnly>
            <Button
              size="compact-md"
              disabled={!choosen || !volunteer}
              leftSection={<icons.Add size={16} />}
              onClick={async () => {
                if (!choosen) return
                await create({ session_id: session.id, user_id: choosen })
              }}
            >
              {t("Add")}
            </Button>
          </AdminOnly>
        )}
      </Group>
    </Filters>
  )
}
